import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { Link, Outlet } from 'react-router-dom'

import { Seo } from '../../components'
import Data from '../../components/aivy-admin/data'
import Logs from '../../components/aivy-admin/logs'
import FeatureBadge from '../../components/feature-badge'
import SelectMenu from '../../components/select-menu'
import Button from '../../components/tailwind/Button'
import PageHeading from '../../components/tailwind/PageHeading'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { query } from '../../graphql'
import { classNames } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { tabsConfig } from '../../helpers/settingsTab'

const { version } = require('../../../package.json')

// Rahmen aller Unterseiten von /settings (Kindrouten in ./sections). Die Route
// füllt den Viewport (fillViewport), Überschrift und Menü bleiben stehen und
// nur der Inhalt rechts scrollt, wie in den Einstellungen von Linear oder
// Notion.
const SettingsLayout = () => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { alert } = useNotificationContext()
  const { restartDashboard } = useAivyContext()
  const { isAdmin } = useUserContext()

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const section = useMatch('/settings/:section')?.params.section

  const [displayLogs, setDisplayLogs] = useState(false)
  const [displayData, setDisplayData] = useState(false)

  const tabs = tabsConfig(t, section)
  const currentTab = tabs.find(({ current }) => current)

  // Der Scroll-Container bleibt beim Wechsel der Unterseite bestehen und
  // behielte sonst die alte Position. Layout-Effekt, damit ein Anker der
  // neuen Unterseite (#sample-data) danach noch scrollen kann.
  const scrollRef = useRef(null)
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  const resetCache = () => {
    alert(
      t('settings.reset_cache.modal_title'),
      t('settings.reset_cache.modal_text'),
      [
        {
          onPress: () => {
            trackEvent(trackEvents.CLICK_SETTINGS_RESET_CACHE)
            restartDashboard()
          },
          text: t('settings.reset_cache.modal_action')
        },
        {
          text: t('settings.reset_cache.modal_cancel'),
          style: 'cancel'
        }
      ]
    )
  }

  return (
    <>
      <Seo title={t('seo.titles.settings')} />
      <PageHeading
        title={t('seo.titles.settings')}
        withActions={
          isAdmin ? (
            <div className='ml-auto flex gap-x-1.5'>
              <Button.SecondaryLG
                text='Show Partner'
                onClick={() => setDisplayData(true)}
              />
              <Button.SecondaryLG
                text='Show Logs'
                onClick={() => setDisplayLogs(true)}
              />
            </div>
          ) : undefined
        }
      />
      <div className='px-4 pb-4 sm:px-6 lg:px-8 xl:hidden'>
        <SelectMenu
          id='tabs'
          options={tabs.map((tab) => [tab.to, tab.name])}
          translate={false}
          value={currentTab?.to}
          onChange={(value) => {
            const { id, to } = tabs.find((tab) => tab.to === value)

            trackEvent(trackEvents.PRESSED_SETTINGS_TAB, {
              tab: id
            })

            navigate(to)
          }}
          className='w-full'
        />
      </div>
      <div className='flex min-h-0 flex-1'>
        <nav
          className='hidden shrink-0 flex-col gap-y-2 overflow-y-auto py-6 pl-8 xl:flex'
          aria-label='Tabs'
        >
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className='group flex items-center'
              id={tab.id.toLowerCase()}
              onClick={() =>
                trackEvent(trackEvents.PRESSED_SETTINGS_TAB, {
                  tab: tab.id
                })
              }
            >
              <div
                className={classNames(
                  'mr-1 h-8 w-1 rounded-full',
                  tab.current ? 'bg-blue-500' : 'group-hover:bg-gray-500'
                )}
              />
              <Link
                to={tab.to}
                className={classNames(
                  tab.current
                    ? 'bg-gray-100 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                  'inline-flex w-64 cursor-pointer items-center rounded-md px-4 py-3 text-sm font-medium'
                )}
                aria-current={tab.current ? 'page' : undefined}
              >
                <tab.icon
                  className={classNames(
                    tab.current
                      ? 'text-blue-500'
                      : 'text-gray-400 group-hover:text-gray-500',
                    '-ml-0.5 mr-2 h-5 w-5'
                  )}
                  aria-hidden='true'
                />
                <span className='mr-2 whitespace-nowrap'>{tab.name}</span>
                {tab.badge && <FeatureBadge id={tab.badge} />}
              </Link>
            </div>
          ))}
        </nav>
        {/* relative, damit absolut positionierte Kinder (sr-only) im
            Container bleiben und das Dokument nicht verlängern */}
        <div
          ref={scrollRef}
          className='relative flex min-w-0 flex-1 flex-col overflow-y-auto'
        >
          <div className='w-full max-w-5xl shrink-0 px-4 py-6 sm:px-6 lg:px-8 xl:pl-16 xl:pt-7'>
            <Outlet />
          </div>

          {/* Angaben für den Support, an der Inhaltsspalte ausgerichtet.
              Impressum und Datenschutz liegen im Profilmenü. */}
          <div className='w-full max-w-5xl shrink-0 px-4 pb-8 sm:px-6 lg:px-8 xl:pl-16'>
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 pt-4 text-xs text-gray-500'>
              <span>{`v${version}`}</span>
              <span>
                {t('settings.partner_id', { partner_id: partner.id })}
              </span>
              <button
                type='button'
                onClick={resetCache}
                className='font-medium text-[#1660F3] hover:text-primaryBlue/70'
              >
                {t('settings.reset_cache.action')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Logs
        title='PARTNER-LOGS'
        open={displayLogs}
        setOpen={setDisplayLogs}
        query={() =>
          query({
            query: 'logsByPartner',
            variables: { partner_id: partner.id }
          })
        }
      />
      <Data
        title='PARTNER'
        open={displayData}
        setOpen={setDisplayData}
        data={partner}
      />
    </>
  )
}

export default SettingsLayout
